import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import {
    CornerType,
    ConfigurableOption,
    GuideArmPosition,
    GuideCorner,
    GuidesAndTypeConfig,
    GuideTypeOption,
    GuideType,
    IndividualGuideTypes,
    OptionAvailability,
} from '@/modules/ConfiguratorJoints/composables/useGuidesAndType';

interface Props {
    /** Bend angle picked in step 3 — drives which guide types are offered. */
    angle: number;
    /** Guide type selected from the original all-corners picker. */
    guideType: GuideType | null;
    /** Whether guide selection is managed per individual corner. */
    individualGuidesEnabled: boolean;
    /** Guide type picked for each visual corner selector. */
    individualGuideTypes: IndividualGuideTypes;
    /** Currently selected corner type. */
    cornerType: CornerType | null;
    guidesAndTypeConfig: GuidesAndTypeConfig;
    areDimensionsLimitsMet: boolean;
}

interface SelectableOption<TValue extends string> extends ConfigurableOption<TValue> {
    /** Shown but not selectable when business rules do not currently allow it. */
    disabled: boolean;
}

interface SelectableGuideTypeOption extends GuideTypeOption {
    /** Shown but not selectable when business rules do not currently allow it. */
    disabled: boolean;
}

interface AvailabilityContext {
    angle: number;
    selectedCornerType: CornerType | null;
}

// Generic matcher — the ONLY place that reads the availability schema. No
// option-specific knowledge lives here, so adding/changing an option is a pure
// data edit in `guidesAndTypeConfig`.

export default /*#__PURE__*/_defineComponent({
  __name: 'GuidesAndType',
  props: {
    angle: null,
    guideType: null,
    individualGuidesEnabled: { type: Boolean },
    individualGuideTypes: null,
    cornerType: null,
    guidesAndTypeConfig: null,
    areDimensionsLimitsMet: { type: Boolean }
  },
  emits: ["update:allGuideTypes", "update:individualGuidesEnabled", "update:individualGuideType", "resetGuides", "update:cornerType", "noGuideDimensionLimitClicked"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'update:allGuideTypes', value: GuideType | null): void;
    (event: 'update:individualGuidesEnabled', value: boolean): void;
    (
        event: 'update:individualGuideType',
        position: GuideArmPosition,
        corner: GuideCorner,
        value: GuideType | null,
    ): void;
    (event: 'resetGuides'): void;
    (event: 'update:cornerType', value: CornerType): void;
    (event: 'noGuideDimensionLimitClicked'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    /** Bend angle picked in step 3 — drives which guide types are offered. */
    angle: number;
    /** Guide type selected from the original all-corners picker. */
    guideType: GuideType | null;
    /** Whether guide selection is managed per individual corner. */
    individualGuidesEnabled: boolean;
    /** Guide type picked for each visual corner selector. */
    individualGuideTypes: IndividualGuideTypes;
    /** Currently selected corner type. */
    cornerType: CornerType | null;
    guidesAndTypeConfig: GuidesAndTypeConfig;
    areDimensionsLimitsMet: boolean;
};




// ============================================================================
// GUIDE TYPES AND CORNER TYPES
// ----------------------------------------------------------------------------
// `guidesAndTypeConfig` is the complete, fully-declarative description of every
// guide type and corner type and the exact conditions under which each one is
// offered. There is intentionally NO branching logic in the computed props —
// they only filter this data through the generic `isOptionAvailable` matcher.
// Because every rule lives here as plain data (numbers + string arrays), the
// whole object can be serialised to JSON / exported to Excel as-is.
//
// Availability of an option is the AND of:
//   - angle is within [minAngle, maxAngle]                  (inclusive)
//   - selected corner type is one of `requiredCornerTypes`  (empty = no constraint)
//
// Business rules captured below:
//   Guide types
//     · Wire / Inox guides → offered across the whole 45–90° range for both corner types.
//     · Alu guides         → offered across the whole 45–90° range for Type 2 corners.
//     · No guides          → Type 2 only, once the corner is wide enough (75–90°).
//   Corner types
//     · Type 1 → only on a square corner (exactly 90°). (Not yet used in production.)
//     · Type 2 → always available (any angle, any guide). When it is the only
//                option the dropdown is replaced by a static label.
// ============================================================================
function isOptionAvailable(availability: OptionAvailability, context: AvailabilityContext): boolean {
    const withinAngleRange = context.angle >= availability.minAngle && context.angle <= availability.maxAngle;
    const cornerTypeSatisfied =
        availability.requiredCornerTypes.length === 0 ||
        (context.selectedCornerType !== null && availability.requiredCornerTypes.includes(context.selectedCornerType));
    if (availability.dimensionsLimitsMet !== undefined) {
        const dimensionsLimitsMet = availability.dimensionsLimitsMet === props.areDimensionsLimitsMet;
        return withinAngleRange && cornerTypeSatisfied && dimensionsLimitsMet;
    }
    return withinAngleRange && cornerTypeSatisfied;
}

function deriveGuideTypeOptions(angle: number, cornerType: CornerType | null): SelectableGuideTypeOption[] {
    return props.guidesAndTypeConfig.guideTypes.map((option) => ({
        ...option,
        disabled: !isOptionAvailable(option.availability, { angle, selectedCornerType: cornerType }),
    }));
}

function deriveCornerTypeOptions(angle: number): SelectableOption<CornerType>[] {
    return props.guidesAndTypeConfig.cornerTypes.map((option) => ({
        ...option,
        disabled: !isOptionAvailable(option.availability, { angle, selectedCornerType: null }),
    }));
}

const guideTypeOptions = computed(() => deriveGuideTypeOptions(props.angle, props.cornerType));
const cornerTypeOptions = computed(() => deriveCornerTypeOptions(props.angle));
const guideArmPositions: GuideArmPosition[] = [1, 2];

function onGuideTypeChange(option: SelectableGuideTypeOption) {
    if (option.disabled) {
        if (option.value === 'no-guide' && !props.areDimensionsLimitsMet) {
            emit('noGuideDimensionLimitClicked');
        }

        return;
    }

    emit('update:allGuideTypes', option.value);
}

function onIndividualGuidesToggle(value: boolean) {
    if (!value) {
        emit('resetGuides');
        return;
    }

    emit('update:individualGuidesEnabled', true);
}

function onIndividualGuideTypeChange(position: GuideArmPosition, corner: GuideCorner, value: GuideType) {
    if (guideTypeOptions.value.some((option) => option.value === value && option.disabled)) {
        if (value === 'no-guide' && !props.areDimensionsLimitsMet) {
            emit('noGuideDimensionLimitClicked');
        }

        return;
    }

    emit('update:individualGuideType', position, corner, value);
}

function onCornerTypeChange(value: CornerType) {
    if (cornerTypeOptions.value.some((option) => option.value === value && option.disabled)) {
        return;
    }

    emit('update:cornerType', value);
}

return { __sfc: true,props, emit, isOptionAvailable, deriveGuideTypeOptions, deriveCornerTypeOptions, guideTypeOptions, cornerTypeOptions, guideArmPositions, onGuideTypeChange, onIndividualGuidesToggle, onIndividualGuideTypeChange, onCornerTypeChange }
}

})